// Calendar Event Type Definitions and Colors
export const EVENT_TYPES = {
  FIELD_PRODUCTION: 'field_production',
  REMOTE_PRODUCTION: 'remote_production',
  ANTHEM_STUDIO: 'anthem_studio',
  TRAVEL: 'travel',
  BLOCKED: 'blocked',
  TIME_OFF: 'time_off'
};

export const EVENT_TYPE_CONFIG = {
  [EVENT_TYPES.FIELD_PRODUCTION]: {
    label: 'Field Production',
    value: 'field_production',
    color: 'hsl(158, 80%, 35%)',  // Success green from Bulma config
    className: 'is-success',
    icon: 'video',
    iconStyle: 'fal'
  },
  [EVENT_TYPES.REMOTE_PRODUCTION]: {
    label: 'Remote Production',
    value: 'remote_production',
    color: 'hsl(228.5, 88.4%, 62.9%)',  // Custom blue-purple color
    className: 'is-info',
    icon: 'laptop',
    iconStyle: 'fal'
  },
  [EVENT_TYPES.ANTHEM_STUDIO]: {
    label: 'Anthem Studio',
    value: 'anthem_studio',
    color: 'hsl(278, 99%, 42%)',  // Purple from Bulma config
    className: 'is-purple',
    icon: 'microphone-lines',
    iconStyle: 'fal'
  },
  [EVENT_TYPES.TRAVEL]: {
    label: 'Travel',
    value: 'travel',
    color: 'hsl(34, 100%, 56%)',  // Warning orange from Bulma config
    className: 'is-warning',
    icon: 'plane',
    iconStyle: 'fal'
  },
  [EVENT_TYPES.BLOCKED]: {
    label: 'Blocked Date',
    value: 'blocked',
    color: 'var(--bulma-danger)',  // Use Bulma's danger variable
    className: 'is-danger',
    icon: 'calendar-xmark',
    iconStyle: 'fal'
  },
  [EVENT_TYPES.TIME_OFF]: {
    label: 'Time Off',
    value: 'time_off',
    color: 'hsl(190, 70%, 45%)',  // Teal — distinct from existing green/blue/purple/orange/red
    className: 'is-time-off',
    icon: 'umbrella-beach',
    iconStyle: 'fal'
  }
};

// Get event type options for dropdowns
export const getEventTypeOptions = () => {
  return Object.values(EVENT_TYPE_CONFIG).map(config => ({
    value: config.value,
    label: config.label,
    icon: config.icon,
    iconStyle: config.iconStyle,
    color: config.color
  }));
};

// Get color for event type
export const getEventTypeColor = (type) => {
  const config = EVENT_TYPE_CONFIG[type];
  return config ? config.color : '#48c774'; // Default to green
};

// Get icon for event type (returns full FontAwesome array format)
export const getEventTypeIcon = (type) => {
  const config = EVENT_TYPE_CONFIG[type];
  const icon = config ? config.icon : 'camera';
  const style = config ? config.iconStyle : 'fal';
  return [style, icon];
};

// Get class name for event type
export const getEventTypeClassName = (type) => {
  const config = EVENT_TYPE_CONFIG[type];
  return config ? config.className : 'production-event'; // Default to production
};

// Event types a client can actually book. Blocked dates target this set
// (admins choose a subset, or all of them).
const BOOKABLE_EVENT_TYPES = [
  EVENT_TYPES.FIELD_PRODUCTION,
  EVENT_TYPES.REMOTE_PRODUCTION,
  EVENT_TYPES.ANTHEM_STUDIO
];

// Dropdown/checkbox options for the bookable types (label + icon from config).
export const getBookableEventTypeOptions = () =>
  BOOKABLE_EVENT_TYPES.map(type => ({
    value: type,
    label: EVENT_TYPE_CONFIG[type].label,
    icon: EVENT_TYPE_CONFIG[type].icon,
    iconStyle: EVENT_TYPE_CONFIG[type].iconStyle,
    color: EVENT_TYPE_CONFIG[type].color
  }));

// Does a blocked-date event apply to the given booking type?
// A missing/empty blockedEventTypes array means the date blocks every bookable
// type (back-compat: existing blocked dates created before this field keep
// blocking everything).
export const blockedDateAppliesToType = (blockedEvent, eventType) => {
  const types = blockedEvent?.blockedEventTypes;
  if (!Array.isArray(types) || types.length === 0) return true;
  return types.includes(eventType);
};

// Human-readable summary of which types a blocked date targets.
export const describeBlockedEventTypes = (blockedEvent) => {
  const types = blockedEvent?.blockedEventTypes;
  if (!Array.isArray(types) || types.length === 0) return 'All event types';
  return types
    .map(type => EVENT_TYPE_CONFIG[type]?.label || type)
    .join(', ');
};

// Per-event-type duration limits (client-side enforcement only — admins are exempt)
// Single source of truth: both validateEventDuration and the booking-flow custom
// stepper (TimeSlotPicker) read these bounds, so they can't disagree.
export const EVENT_DURATION_LIMITS = {
  [EVENT_TYPES.REMOTE_PRODUCTION]: { minHours: 0.5, maxHours: 8 }
};

// Podcast recordings (a studio booking carrying a showId) are capped at 1–2h.
// Other studio use books like a field production.
const PODCAST_DURATION_LIMITS = { minHours: 1, maxHours: 2 };
const isPodcastSession = (eventType, showId) => eventType === EVENT_TYPES.ANTHEM_STUDIO && !!showId;

export const durationLimitsFor = (eventType, showId) =>
  isPodcastSession(eventType, showId) ? PODCAST_DURATION_LIMITS : EVENT_DURATION_LIMITS[eventType];

// Length presets shown in the booking wizard's time-picker step.
// Shape: { label, sub?, hours?, custom? }. Podcast sessions use concrete hours,
// everything else uses named units plus a custom escape hatch.
const PODCAST_LENGTH_PRESETS = [
  { label: '1 hour',    hours: 1   },
  { label: '1.5 hours', hours: 1.5 },
  { label: '2 hours',   hours: 2   }
];

const EVENT_LENGTH_PRESETS = {
  [EVENT_TYPES.FIELD_PRODUCTION]: [
    { label: 'Half day', sub: '4h', hours: 4 },
    { label: 'Full day', sub: '8h', hours: 8 },
    { label: 'Custom',   custom: true        }
  ],
  [EVENT_TYPES.REMOTE_PRODUCTION]: [
    { label: 'Half day', sub: '4h', hours: 4 },
    { label: 'Full day', sub: '8h', hours: 8 },
    { label: 'Custom',   custom: true        }
  ]
};

export const CUSTOM_LENGTH_GRANULARITY_MIN = 30;
export const CUSTOM_LENGTH_MIN_HOURS = 1;
export const CUSTOM_LENGTH_MAX_HOURS = 9;

// Lead-time rule for client booking: the booking must be at least this many
// calendar days ahead of today. Days 0..MIN_DAYS_AHEAD-1 are blocked.
// Default 3 = today/+1/+2 blocked, +3 is the first bookable day.
export const MIN_DAYS_AHEAD = 3;

// Filter the length-preset list based on the contract's metric.
// - Days + half days disallowed → Full day only (caller may also hide the row)
// - Days + half days allowed    → Half day + Full day (no Custom)
// - Hours metric (or no contract context) → unchanged
// Podcast sessions always use concrete hours and ignore contract metric;
// other studio bookings follow the field-production presets.
export const getLengthPresets = (eventType, { useDays, allowHalfDays, range, showId } = {}) => {
  if (range) return getRangePresets(range);
  if (isPodcastSession(eventType, showId)) return PODCAST_LENGTH_PRESETS;
  const base = EVENT_LENGTH_PRESETS[eventType] || EVENT_LENGTH_PRESETS[EVENT_TYPES.FIELD_PRODUCTION];
  // Remote production always offers the full range incl. custom (0.5–8h),
  // regardless of the days/hours metric — only the deduction differs.
  if (eventType === EVENT_TYPES.REMOTE_PRODUCTION) return base;
  if (!useDays) return base;
  if (!allowHalfDays) return base.filter(p => p.label === 'Full day');
  return base.filter(p => !p.custom);
};

// Admin-set length for a client-dated event with no contract, stored on the
// event as `clientLength: { metric: 'hours' | 'days', min, max }` (min/max in
// the metric's unit). Returns hour bounds for the booking picker, or null.
export const HOURS_PER_DAY = 8;
export const clientLengthRange = (clientLength) => {
  const { metric, min, max } = clientLength || {};
  if (!(min > 0) || !(max >= min)) return null;
  const factor = metric === 'days' ? HOURS_PER_DAY : 1;
  return { minHours: min * factor, maxHours: max * factor, custom: metric !== 'days' };
};

// Presets inside an admin range. Hour ranges stay in hours: one bounded stepper
// (or a single fixed length). Day ranges offer the Half/Full day presets that fit.
const hoursLabel = (h) => `${h} hour${h === 1 ? '' : 's'}`;
const getRangePresets = ({ minHours, maxHours, custom }) => {
  if (custom) {
    return minHours < maxHours
      ? [{ label: `${minHours}–${hoursLabel(maxHours)}`, custom: true }]
      : [{ label: hoursLabel(minHours), hours: minHours }];
  }
  const named = EVENT_LENGTH_PRESETS[EVENT_TYPES.FIELD_PRODUCTION]
    .filter(p => !p.custom && p.hours >= minHours && p.hours <= maxHours);
  return named.length ? named : [{ label: hoursLabel(minHours), hours: minHours }];
};

// Validate an event's duration against its type's limits (or `limits` given).
// Returns { valid: true } if the type has no limits or the duration is within range,
// otherwise { valid: false, error } with a user-facing message.
export const validateEventDuration = (eventType, durationHours, limits = EVENT_DURATION_LIMITS[eventType]) => {
  if (!limits) return { valid: true };
  if (typeof durationHours !== 'number' || !isFinite(durationHours)) {
    return { valid: true };
  }
  const label = EVENT_TYPE_CONFIG[eventType]?.label || 'Event';
  if (durationHours < limits.minHours) {
    const unit = limits.minHours === 1 ? 'hour' : 'hours';
    return { valid: false, error: `${label} bookings require a minimum of ${limits.minHours} ${unit}.` };
  }
  if (durationHours > limits.maxHours) {
    return { valid: false, error: `${label} bookings cannot exceed ${limits.maxHours} hours.` };
  }
  return { valid: true };
};

// Migrate old event type to new format
export const migrateEventType = (oldType) => {
  if (!oldType || oldType === 'production') {
    return EVENT_TYPES.FIELD_PRODUCTION;
  }
  // Check if it's already a valid new type
  if (Object.values(EVENT_TYPES).includes(oldType)) {
    return oldType;
  }
  // Default to field production for unknown types
  return EVENT_TYPES.FIELD_PRODUCTION;
};